import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import api from '../api/axios';

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [defaultGroups, setDefaultGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDesc, setNewGroupDesc] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    fetchGroups();
    fetchDefaultGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const res = await api.get('/groups');
      setGroups(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchDefaultGroups = async () => {
    try {
      const res = await api.get('/admin/default-groups');
      setDefaultGroups(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const handleCreateDefaultGroup = async () => {
    if (!newGroupName.trim()) {
      setCreateError('Group name is required');
      return;
    }
    setCreating(true);
    setCreateError('');
    try {
      const res = await api.post('/admin/default-groups', {
        name: newGroupName.trim(),
        description: newGroupDesc.trim(),
      });
      setDefaultGroups(prev => [res.data, ...prev]);
      setShowCreateModal(false);
      setNewGroupName('');
      setNewGroupDesc('');
    } catch (e) {
      setCreateError(e.response?.data?.message || 'Failed to create group');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteDefaultGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this default group?')) return;
    try {
      await api.delete(`/admin/default-groups/${groupId}`);
      setDefaultGroups(prev => prev.filter(g => g._id !== groupId));
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to delete group');
    }
  };

  const filtered = groups.filter(g =>
    g.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (ts) => ts ? new Date(ts).toLocaleDateString() : '';

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-dark)' }}>
      <Navbar />
      <div className="page-wrapper">
        
        {/* Default Groups Section */}
        <div className="mb-4">
          <div className="header-row" style={{ marginBottom: 24 }}>
            <div>
              <h2 className="page-title" style={{ fontSize: 24 }}>Default Groups</h2>
              <p className="page-subtitle">All admins are automatically added to these groups</p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary"
            >
              <span style={{ fontSize: 16 }}>+</span> Create Default Group
            </button>
          </div>

          {defaultGroups.length === 0 ? (
            <div className="glass-panel" style={{
              padding: 40, textAlign: 'center', color: 'var(--text-muted)',
              border: '1px dashed rgba(255,255,255,0.2)'
            }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>👥</div>
              <div style={{ fontWeight: 600, color: '#fff', fontSize: 16, marginBottom: 4 }}>No default groups yet</div>
              <div style={{ fontSize: 14 }}>Create a default group to automatically add all admins</div>
            </div>
          ) : (
            <div className="grid-cards">
              {defaultGroups.map(group => (
                <div key={group._id} className="card" style={{ padding: 20, position: 'relative' }}>
                  <span className="badge badge-default" style={{ position: 'absolute', top: 12, right: 12 }}>DEFAULT</span>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                    <div style={{
                      width: 50, height: 50, borderRadius: '50%',
                      background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.3)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 22, flexShrink: 0
                    }}>👥</div>
                    <div>
                      <div style={{ fontWeight: 700, color: '#fff', fontSize: 16, letterSpacing: '-0.01em' }}>{group.name}</div>
                      {group.description && (
                        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>{group.description}</div>
                      )}
                    </div>
                  </div>

                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16, display: 'flex', gap: 12 }}>
                    <span className="badge" style={{ background: 'rgba(255,255,255,0.05)', color: '#fff' }}>
                      {group.participants?.length || 0} admins
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center' }}>Created {formatDate(group.createdAt)}</span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 6 }}>
                    {(group.participants || []).slice(0, 6).map((member) => (
                      <div key={member._id} title={member.name} style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontSize: 12, fontWeight: 700,
                        border: '2px solid var(--bg-panel)',
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                      }}>
                        {member.avatar ? <img src={member.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (member.name?.[0] || '?').toUpperCase()}
                      </div>
                    ))}
                    {(group.participants?.length || 0) > 6 && (
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, color: '#fff', fontWeight: 700, border: '2px solid var(--bg-panel)'
                      }}>+{group.participants.length - 6}</div>
                    )}
                  </div>

                  <button onClick={() => handleDeleteDefaultGroup(group._id)} className="btn btn-danger" style={{ width: '100%' }}>
                    Delete Group
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ height: 1, background: 'rgba(255,255,255,0.05)', margin: '40px 0' }} />

        {/* All Groups Section */}
        <div className="header-row" style={{ marginBottom: 24 }}>
          <div>
            <h2 className="page-title" style={{ fontSize: 24 }}>All Groups</h2>
            <p className="page-subtitle">{groups.length} groups total existing</p>
          </div>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 16, top: 12, fontSize: 14 }}>🔍</span>
            <input
              type="text"
              className="input-field"
              placeholder="Search groups..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ width: 300, paddingLeft: 44, borderRadius: 24 }}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: selectedGroup ? '1fr 420px' : '1fr', gap: 24, alignItems: 'start' }}>
          
          <div className="table-container">
            {loading ? (
              <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading groups...</div>
            ) : filtered.length === 0 ? (
              <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>👥</div>
                <div style={{ fontSize: 16, color: '#fff' }}>No groups found matching "{searchQuery}"</div>
              </div>
            ) : (
              <table className="modern-table">
                <thead>
                  <tr>
                    {['Group Info', 'Members', 'Admins', 'Created', ''].map((h, i) => <th key={i}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(group => (
                    <tr key={group._id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                          <div style={{
                            width: 44, height: 44, borderRadius: '12px',
                            background: group.isDefault ? 'linear-gradient(135deg, #f59e0b, #d97706)' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                          }}>
                            {group.avatar ? <img src={group.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px' }} /> : <span style={{ color: '#fff', fontSize: 18 }}>👥</span>}
                          </div>
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontWeight: 600, color: '#fff', fontSize: 15 }}>{group.name}</span>
                              {group.isDefault && <span className="badge badge-default">DEFAULT</span>}
                            </div>
                            {group.description && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{group.description}</div>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="badge badge-admin">{group.participants?.length || 0} members</span>
                      </td>
                      <td>
                        <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                          {(group.admins || []).map(a => a.name).join(', ') || '—'}
                        </div>
                      </td>
                      <td style={{ fontSize: 13, color: 'var(--text-muted)' }}>{formatDate(group.createdAt)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          className={selectedGroup?._id === group._id ? 'btn btn-outline' : 'btn btn-primary'}
                          onClick={() => setSelectedGroup(selectedGroup?._id === group._id ? null : group)}
                          style={{ padding: '6px 16px', fontSize: 12 }}
                        >
                          {selectedGroup?._id === group._id ? 'Close' : 'View Details'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {selectedGroup && (
            <div className="glass-panel animate-in" style={{ padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 56, height: 56, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>
                    {selectedGroup.avatar ? <img src={selectedGroup.avatar} alt="" style={{ width: '100%', height: '100%', borderRadius: 16, objectFit: 'cover' }} /> : '👥'}
                  </div>
                  <div>
                    <h3 style={{ margin: 0, color: '#fff', fontSize: 20, letterSpacing: '-0.01em' }}>{selectedGroup.name}</h3>
                    {selectedGroup.isDefault && <span className="badge badge-default" style={{ marginTop: 6 }}>DEFAULT GROUP</span>}
                  </div>
                </div>
                <button onClick={() => setSelectedGroup(null)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
              </div>

              {selectedGroup.description && (
                <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', padding: 16, borderRadius: 12, fontSize: 14, color: 'var(--text-muted)', marginBottom: 24, lineHeight: 1.5 }}>
                  {selectedGroup.description}
                </div>
              )}

              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', letterSpacing: 1, marginBottom: 16, textTransform: 'uppercase', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Group Members</span>
                  <span className="badge badge-admin">{selectedGroup.participants?.length || 0}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 400, overflowY: 'auto', paddingRight: 8 }}>
                  {(selectedGroup.participants || []).map(member => {
                    const isAdmin = (selectedGroup.admins || []).some(a => a._id === member._id);
                    return (
                      <div key={member._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 10, background: 'rgba(0,0,0,0.2)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.02)' }}>
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, fontWeight: 600 }}>
                          {member.avatar ? <img src={member.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} /> : (member.name?.[0] || '?').toUpperCase()}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: '#fff' }}>{member.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{member.phone}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          {isAdmin && <span className="badge badge-success">Admin</span>}
                          {member.isOnline && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', boxShadow: '0 0 8px #10b981' }} title="Online"></span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.05)', fontSize: 12, color: 'var(--text-muted)' }}>
                Created on {formatDate(selectedGroup.createdAt)} {selectedGroup.createdBy && `by ${selectedGroup.createdBy.name}`}
              </div>
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ margin: 0, color: '#fff', fontSize: 24, letterSpacing: '-0.02em' }}>Create Default Group</h2>
              <button onClick={() => { setShowCreateModal(false); setCreateError(''); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 24, cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)', padding: 16, borderRadius: 12, fontSize: 14, color: '#93c5fd', marginBottom: 24, display: 'flex', gap: 12 }}>
              <span style={{ fontSize: 20 }}>ℹ️</span>
              <div>All current and future admins will be automatically added to this group upon creation.</div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label className="input-label">Group Name <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="text"
                className="input-field"
                value={newGroupName}
                onChange={e => setNewGroupName(e.target.value)}
                placeholder="e.g. Platform Operations"
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label className="input-label">Description (Optional)</label>
              <textarea
                className="input-field"
                value={newGroupDesc}
                onChange={e => setNewGroupDesc(e.target.value)}
                placeholder="Describe the purpose of this group..."
                rows={4}
                style={{ resize: 'vertical' }}
              />
            </div>

            {createError && (
              <div style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#f87171', padding: '12px 16px', borderRadius: 8, marginBottom: 24, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span>⚠️</span> {createError}
              </div>
            )}

            <div style={{ display: 'flex', gap: 12 }}>
              <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => { setShowCreateModal(false); setCreateError(''); }}>
                Cancel
              </button>
              <button onClick={handleCreateDefaultGroup} disabled={creating} className="btn btn-primary" style={{ flex: 2 }}>
                {creating ? 'Creating Group...' : 'Create Default Group'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
